import { Alert, Button, Card, Form, Input, Typography } from 'antd'
import { useState } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../AuthContext'
import { resendVerification } from '../api'
import type { SaasApiError } from '../api'

interface FormValues {
  email: string
  password: string
}

function mapLoginError(err: SaasApiError): string {
  if (err.code === 'email_not_verified') return 'Please verify your email first.'
  if (err.code === 'invalid_credentials' || err.status === 401) return '邮箱或密码不正确'
  if (err.code === 'auth_service_unavailable' || err.status === 503) {
    return '暂时无法连接登录服务，请稍后重试'
  }
  if (err.code === 'TRANSPORT_ERROR' || err.status == null) {
    return '暂时无法连接登录服务，请稍后重试'
  }
  if (err.status != null && err.status >= 500) return '暂时无法连接登录服务，请稍后重试'
  // Never surface CCA/business sync wording on the auth surface.
  const msg = err.message || 'Login failed'
  if (/最新状态|继续尝试同步|Could not refresh the latest status/i.test(msg)) {
    return '暂时无法连接登录服务，请稍后重试'
  }
  return msg
}

/**
 * The ONLY place a browser exchanges a password for a session (spec §13)
 * — no `?role=admin`, no hardcoded switch button, no localStorage fake
 * auth anywhere else in this app. After login, routes based on the
 * real identity returned by the backend: Platform Admin -> `/platform`,
 * Tenant Admin (has >=1 workspace membership) -> `/workspace`.
 */
export default function LoginPage() {
  const { login, me } = useAuth()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Phase S1 — set only on the specific `email_not_verified` login error,
  // so the "resend" action is never shown for a plain wrong-password 401.
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null)
  const [resent, setResent] = useState(false)
  // LOCAL-AUTH-A1 — only set when resend API returns development_verification_url
  // (APP_ENV local/development). Preview/Production never populate this.
  const [developmentVerificationUrl, setDevelopmentVerificationUrl] = useState<string | null>(null)
  const navigate = useNavigate()
  const location = useLocation()
  const from = (location.state as { from?: string } | null)?.from
  const justVerified = new URLSearchParams(location.search).get('verified') === '1'
  // Password Recovery — ResetPasswordPage redirects here with this query
  // param on success (spec: "/login?password_reset=1"). One-time banner,
  // never persisted anywhere — a page refresh drops it along with the
  // query string once the user navigates away.
  const justResetPassword = new URLSearchParams(location.search).get('password_reset') === '1'
  const addingDeviceAccount = new URLSearchParams(location.search).get('addAccount') === '1'

  // VISTOERA_LOCAL_AUTH_STABILITY_A1 — `from` (location.state.from,
  // normally "send the user back where they tried to go before we made
  // them log in") can be stale: RequireAuth's own reactive "session just
  // became invalid" redirect can race ahead of an explicit logout's own
  // navigation and stamp `from` with the JUST logged-out user's own
  // Workspace path. If a *different* account then logs in on that same
  // browser tab, blindly honoring `from` would send them straight to a
  // Workspace path they aren't a member of (the backend still 403s it —
  // no data/tenant-isolation leak — but it is a broken landing page
  // instead of their own Workspace). Only trust a `/workspace/<id>/...`
  // `from` when the identity it will be applied to (either already
  // logged in, in the guard below, or freshly authenticated, in
  // `onFinish`) is actually a member of that exact Workspace id. Both
  // call sites below must apply this check — the guard fires on its own
  // the instant `me` becomes truthy (a plain re-render can win the race
  // against `onFinish`'s own explicit `navigate()` call further down).
  const usableFrom = (workspaces: { workspace_id: string }[]): string | undefined => {
    const workspaceMatch = from?.match(/^\/workspace\/([^/]+)/)
    if (!workspaceMatch) return from
    return workspaces.some((w) => w.workspace_id === workspaceMatch[1]) ? from : undefined
  }

  if (me) {
    return <Navigate to={usableFrom(me.workspaces) || (me.is_platform_admin ? '/platform' : '/workspace')} replace />
  }

  const onFinish = async (values: FormValues) => {
    setSubmitting(true)
    setError(null)
    setUnverifiedEmail(null)
    setResent(false)
    setDevelopmentVerificationUrl(null)
    try {
      const email = values.email.trim()
      const result = await login(email, values.password)
      const destination = usableFrom(result.workspaces)
      navigate(destination || (result.is_platform_admin ? '/platform' : '/workspace'), { replace: true })
    } catch (err) {
      const apiError = err as SaasApiError
      if (apiError.code === 'email_not_verified') {
        setUnverifiedEmail(values.email.trim())
        setError('Please verify your email first.')
      } else {
        setError(mapLoginError(apiError))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const onResend = async () => {
    if (!unverifiedEmail) return
    try {
      const { data } = await resendVerification(unverifiedEmail)
      setDevelopmentVerificationUrl(data.development_verification_url?.trim() || null)
    } catch {
      setDevelopmentVerificationUrl(null)
    }
    setResent(true)
  }

  return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f2f5' }}>
      <Card style={{ width: 380 }}>
        <Typography.Title level={3} style={{ marginBottom: 4 }}>
          Vistoera Cloud Admin
        </Typography.Title>
        <Typography.Text type="secondary">Sign in to continue</Typography.Text>
        <Form layout="vertical" onFinish={onFinish} style={{ marginTop: 24 }} disabled={submitting}>
          {justVerified && !error && (
            <Alert type="success" message="Email verified — please log in." showIcon style={{ marginBottom: 16 }} />
          )}
          {justResetPassword && !error && (
            <Alert
              type="success"
              message="Password updated successfully. Please sign in."
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          {addingDeviceAccount && !error && (
            <Alert
              type="info"
              message="Sign in to add this account on this device. You can switch between saved accounts later without re-entering passwords."
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          {error && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message={error}
              description={
                unverifiedEmail ? (
                  resent ? (
                    developmentVerificationUrl ? (
                      <div>
                        <div>本地开发环境未发送真实邮件。请使用新的验证链接：</div>
                        <Typography.Paragraph copyable style={{ marginBottom: 8, wordBreak: 'break-all' }}>
                          {developmentVerificationUrl}
                        </Typography.Paragraph>
                        <a href={developmentVerificationUrl}>
                          <Button type="primary" size="small">
                            验证邮箱
                          </Button>
                        </a>
                      </div>
                    ) : (
                      'A new verification email is on its way.'
                    )
                  ) : (
                    <Button type="link" size="small" style={{ padding: 0 }} onClick={onResend}>
                      Resend verification email
                    </Button>
                  )
                ) : null
              }
            />
          )}
          <Form.Item name="email" label="Email" rules={[{ required: true, message: 'Email is required' }]}>
            <Input autoFocus autoComplete="username" placeholder="you@example.com" />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
            <Typography.Text>
              <span style={{ color: '#ff4d4f', fontFamily: 'SimSun, sans-serif', marginRight: 4 }}>*</span>
              Password
            </Typography.Text>
            <Link to="/forgot-password" data-testid="login-forgot-password">
              Forgot password?
            </Link>
          </div>
          <Form.Item name="password" rules={[{ required: true, message: 'Password is required' }]}>
            <Input.Password autoComplete="current-password" placeholder="••••••••" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 8 }}>
            <Button type="primary" htmlType="submit" block loading={submitting}>
              Sign in
            </Button>
          </Form.Item>
          <Typography.Text type="secondary">
            New to Vistoera? <Link to="/signup">Create an account</Link>
          </Typography.Text>
        </Form>
      </Card>
    </div>
  )
}
